import { useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { uid } from '../../../../lib/uid';
import { surveyTrainingRecommendationsApi } from '../surveyApi';
import { sampleTrainingRecommendationText, sampleTrainingTitle } from '../utils/sampleDevelopmentPlan';
import {
  blankTraining,
  isBlankTraining,
  isUntitledTraining,
  toTrainingRequest,
  type DraftTraining,
} from '../utils/trainingRecommendationRows';
import { TrainingRecommendationsGrid } from './TrainingRecommendationsGrid';

const ID = 'training-recommendation-add';

/**
 * A plan's Training Recommendations › Create — the inline-add grid (product call, 13 Oct 2026; it
 * was one title over a rich-text editor): several recommendations in one go, a row each. EDITING
 * one stays on its own form, where its details are a document. Writing takes `surveys.plan`.
 *
 * Each row is its own record with its own approval, so the rows are written ONE AT A TIME, in
 * order. If one is refused, the rows before it are already saved and LEAVE the grid; the refused
 * row and the rest stay, with the server's reason — nothing is saved twice on a second Submit.
 */
export function SurveyTrainingRecommendationsAddModal({
  open,
  planId,
  onClose,
  onSaved,
}: {
  open: boolean;
  planId: string;
  onClose: () => void;
  /** After at least one row was saved — also when a later row was refused. */
  onSaved: () => void;
}) {
  const toast = useToast();

  const [rows, setRows] = useState<DraftTraining[]>(() => [blankTraining()]);
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const filled = rows.filter((t) => !isBlankTraining(t));
  const canSave = filled.length > 0 && !rows.some(isUntitledTraining) && confirmed && !saving;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    let saved = 0;

    try {
      for (const row of filled) {
        await surveyTrainingRecommendationsApi.create(planId, toTrainingRequest(row));
        saved++;
      }

      toast.success(saved === 1 ? 'Added the training recommendation.' : `Added ${saved} training recommendations.`);
      onSaved();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError('The training recommendation could not be saved.', 0));

      if (saved > 0) {
        // The saved rows leave the grid, so a second Submit cannot write them again.
        const done = new Set(filled.slice(0, saved).map((t) => t.key));

        setRows((current) => current.filter((t) => !done.has(t.key)));
        toast.success(saved === 1 ? 'Added 1 training recommendation before the one refused.' : `Added ${saved} training recommendations before the one refused.`);
        setConfirmed(false);
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New training recommendations"
      size="2xl"
      id={`${ID}-modal`}
      autogenerate={{
        onGenerate: () =>
          setRows([0, 1].map(() => ({ key: uid(), title: sampleTrainingTitle(), details: sampleTrainingRecommendationText() }))),
        disabled: saving,
      }}
      confirm={{ id: `${ID}-confirm`, checked: confirmed, onChange: setConfirmed, disabled: saving }}
      footer={
        <>
          <Button id={`${ID}-cancel`} variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id={`${ID}-submit`}
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        <TrainingRecommendationsGrid idPrefix={ID} rows={rows} onChange={setRows} disabled={saving} />
      </div>
    </Modal>
  );
}
